import React, { useRef, useState, useCallback, useMemo } from 'react';
import { Icon, ContextualMenu, IContextualMenuItem } from '@fluentui/react';
import { t } from 'i18next';
import { RefreshPageDialog } from '../Common/RefreshPageDialog';
import TimeTaxSupportSystemClient from '../../system';
import { User } from '../../system/User';
import './LanguageSelector.css';
import i18n from '../../i18n';

interface ILanguageSelectorProps {
  system: TimeTaxSupportSystemClient;
  user: User;
  changeBlockUI: (v: boolean) => void;
}

const LanguageSelector: React.FC<ILanguageSelectorProps> = ({ system, user, changeBlockUI }) => {
  const languages = system.getLanguageSystem().getLanguages()
  const buttonRef = useRef<HTMLDivElement>(null);
  const [selectedKey, setSelectedKey] = useState<string>(user.getLenguage().trad_nombre.toLowerCase());
  const [showMenu, setShowMenu] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const selectedOption = useMemo(() => languages[selectedKey], [languages, selectedKey]);
  
  const handleSelectLanguage = async (key: string) => {
    if (key === selectedKey) return;
    setSelectedKey(key);
    setShowMenu(false);
    changeBlockUI(true);
    try {
      // Actualizar el idioma del sistema en tiempo real
      await Promise.all([
        i18n.changeLanguage(key),
        system.getLanguageSystem().updatedUserLanguage(key, user.getUserId()),
      ]);
    } catch (error) {
      console.error('Error guardando el idioma:', error);
    } finally {
      changeBlockUI(false);
    }

    setShowDialog(true);
  };

  const handleDialogClose = (confirmed: boolean) => {
    if (confirmed) {
      window.location.reload();
    } else {
      setShowDialog(false);
    }
  };

  const onShowMenu = useCallback((ev: React.MouseEvent<HTMLElement>) => {
    ev.preventDefault();
    setShowMenu(true);
  }, []);

  const onHideMenu = useCallback(() => {
    setShowMenu(false);
  }, []);

  const menuItems: IContextualMenuItem[] = useMemo(
    () =>
      Object.entries(languages).map(([key, value]):IContextualMenuItem => ({
        key,
        text: value.name,
        iconProps: {},
        onClick: () => {
          handleSelectLanguage(key);
        },
        canCheck: true,
        checked: selectedKey === key,
        disabled: selectedKey === key,
        onRenderIcon: () => (
         <p>
          {key.toUpperCase()}
         </p>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedKey, languages],
  );

  return (
    <div style={{ margin: 0 }}>
      <div className="language-selector" ref={buttonRef}>
        <button className="language-selector-button" onClick={onShowMenu}>
           <p>
            <span className='language-selector-parenthesis'>(</span>
            {selectedKey.toUpperCase()}
            <span className='language-selector-parenthesis'>)</span>
           </p>
          <p className="language-label">{selectedOption?.name || 'Seleccione'}</p>
        </button>
        <ContextualMenu title={t('Idioma')} hidden={!showMenu} target={buttonRef.current} items={menuItems} onDismiss={onHideMenu} />
      </div>
      <RefreshPageDialog show={showDialog} onClose={handleDialogClose} />
    </div>
  );
};

export default LanguageSelector;
